import { useQuery } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";
import {
  artistAccessCarouselSchema,
  type ArtistAccessCarousel,
} from "@libs/wire";
import { waitForAuthReady } from "@utils/auth/waitForAuthReady";
import { useData } from "../../runtime/provider";
import type { DataSession } from "../../data/core/session";
import { isAccessNetworkError } from "./accessCache";
import { InvalidOptionsError } from "@utils/InvalidOptionsError";
import { type RouterOutputs, trpc } from "@utils/trpc";

type RouterOutput = RouterOutputs["accesses"]["getCarouselByArtistId"];
type _WireSchemaSubset = ArtistAccessCarousel extends RouterOutput
  ? true
  : never;
type _WireSchemaSuperset = RouterOutput extends ArtistAccessCarousel
  ? true
  : never;
const _wireSchemaDriftCheck: [_WireSchemaSubset, _WireSchemaSuperset] = [
  true,
  true,
];
void _wireSchemaDriftCheck;

export const artistAccessCarouselQueryKey = (artistId: string) =>
  getQueryKey(trpc.accesses.getCarouselByArtistId, { artistId });

/**
 * Public, viewer-sensitive query.
 *
 * Anonymous callers are supported, but a restoring logged-in session must
 * wait until Firebase can attach its token; otherwise the response can include
 * music the viewer has already unlocked.
 */
export const artistAccessCarouselQuery = (
  data: DataSession,
  artistId: string,
) =>
  data.cache.durableQueryOptions({
    fallbackOnError: isAccessNetworkError,
    queryKey: artistAccessCarouselQueryKey(artistId),
    queryFn: async (): Promise<ArtistAccessCarousel> => {
      if (!artistId) {
        throw new InvalidOptionsError("artistAccessCarouselQuery");
      }
      await waitForAuthReady(data.auth);
      return data.api.accesses.getCarouselByArtistId.query({
        artistId,
      });
    },
    schema: artistAccessCarouselSchema,
  });

export const useArtistAccessCarousel = (artistId: string) => {
  const data = useData();
  const query = useQuery({
    ...artistAccessCarouselQuery(data, artistId),
    enabled: Boolean(artistId),
  });

  return { query };
};
